import { OneHourInMillis, OneSecondInMillis } from '@racemap/utilities/consts/time';
import { downloadBlob } from '@racemap/utilities/functions/download';
import { z } from 'zod';

const POLL_INTERVAL_MS = 3 * OneSecondInMillis;
/** Must match FINISHED_GRACE_MS in gears-api's raceVideo controller. */
export const RACE_VIDEO_GRACE_MS = 2 * OneHourInMillis;

const ResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ready'), url: z.string() }),
  z.object({ status: z.literal('unavailable'), reason: z.string() }),
]);

export type RaceVideoAnswer =
  | { status: 'rendering' }
  | { status: 'ready'; url: string }
  | { status: 'unavailable'; reason: string }
  | { status: 'notFinished' }
  | { status: 'error' };

export function raceVideoFileName(starter: { id: string; name?: string | null }): string {
  const name = (starter.name ?? starter.id).replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');
  return `${name || 'race'}-race-video.mp4`;
}

const raceVideoPath = (eventId: string, starterId: string) =>
  `/api/events/${eventId}/starters/${starterId}/race-video`;

/** The link to share: opening it in a browser shows the video, building it on the first visit. */
export const raceVideoLink = (eventId: string, starterId: string) =>
  `${window.location.origin}${raceVideoPath(eventId, starterId)}`;

/** One look at a starter's race video. Starts the render when there is none yet. */
async function fetchRaceVideo(
  eventId: string,
  starterId: string,
  signal?: AbortSignal,
): Promise<RaceVideoAnswer> {
  const res = await fetch(raceVideoPath(eventId, starterId), {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (res.status === 202) return { status: 'rendering' };
  if (res.status === 409) return { status: 'notFinished' };
  if (!res.ok && res.status !== 422) return { status: 'error' };
  const parsed = ResponseSchema.safeParse(await res.json().catch(() => null));
  return parsed.success ? parsed.data : { status: 'error' };
}

const pause = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timeoutId);
      reject(signal.reason);
    });
  });

/** Polls until the video is rendered, or known not to exist. */
export async function waitForRaceVideo(
  eventId: string,
  starterId: string,
  signal?: AbortSignal,
  onRendering?: () => void,
): Promise<Exclude<RaceVideoAnswer, { status: 'rendering' }>> {
  for (;;) {
    const answer = await fetchRaceVideo(eventId, starterId, signal);
    if (answer.status !== 'rendering') return answer;
    onRendering?.();
    await pause(POLL_INTERVAL_MS, signal);
  }
}

/** Waits for a starter's video and saves it. Returns why there is none, or null once saved. */
export async function saveRaceVideo(
  eventId: string,
  starter: { id: string; name?: string | null },
  signal?: AbortSignal,
): Promise<string | null> {
  const answer = await waitForRaceVideo(eventId, starter.id, signal);
  if (answer.status === 'unavailable') return answer.reason;
  if (answer.status !== 'ready') return answer.status;
  try {
    const res = await fetch(answer.url, { signal });
    if (!res.ok) return 'download_failed';
    downloadBlob(await res.blob(), raceVideoFileName(starter));
    return null;
  } catch {
    if (signal?.aborted) throw signal.reason;
    return 'download_failed';
  }
}
